import axios, { AxiosError } from "axios";
import { plainToInstance } from "class-transformer";
import { AgentConfigurationDto } from "healthex-common/agentconfig.dtos";
import { AgentDeploymentConfigDto } from "healthex-common/agentdeploymentconfig.dtos";
import { AgentStatus } from "healthex-common/agentstatus.dtos";
import { APIKeysResult } from "healthex-common/apikeys.dtos";
import { DataSourceDtoWithInProgressClassification, DatasetDto, TableDto, } from "healthex-common/datawarehouse.dtos";
import { OrganizationUpdateFailureReason } from "healthex-common/organization.dtos";
import { err, voidResult } from "healthex-common/result";
import { EpicClientIds } from "healthex-common/smart.dtos";
import { asEnum } from "healthex-common/utils";
import { authenticatedApiConfig, generateURL } from "./BaseApiService";
import { AWSDataDeliveryInfoDto } from "healthex-common/document.retrieval.dtos";
const _INGEST_TYPE_VALUES = ["redox", "epic", "bundle"];
export const INGEST_TYPE_VALUES = [..._INGEST_TYPE_VALUES];
export function asIngestType(input) {
    if (INGEST_TYPE_VALUES.includes(input)) {
        return input;
    }
    else {
        return null;
    }
}
export class ProvisioningService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async createOrganization(input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/organizations"), input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationById(id) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId", { orgId: id }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationGoLiveChecklist(id) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/golive/checklist", { orgId: id }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationPublicProfileById(id) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/public", { orgId: id }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizations(type) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations");
        if (type) {
            url += `?type=${type}`;
        }
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationPublicProfiles(type) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations/public");
        if (type) {
            url += `?type=${type}`;
        }
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationPublicProfileByVanitySubdomain(vanitySubdomain) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/public?vanitySubdomain=${vanitySubdomain}`), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateOrganizationById(id, update) {
        await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId", { orgId: id }), update, authenticatedApiConfig(this.authenticationProvider));
    }
    async doesInviteExistForUser(userId, orgId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId/invited", { orgId, userId }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateOrganizationDetails(id, update) {
        var _a;
        try {
            await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/details", { orgId: id }), update, authenticatedApiConfig(this.authenticationProvider));
        }
        catch (e) {
            if (axios.isAxiosError(e) && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data)) {
                const errorData = e.response.data;
                if (errorData.message) {
                    const failureReason = asEnum(OrganizationUpdateFailureReason)(errorData.message);
                    if (failureReason) {
                        return failureReason;
                    }
                    else {
                        throw e;
                    }
                }
            }
        }
        return null;
    }
    async getOrganizationLogoUploadUrl(id, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/logoUploadUrl", { orgId: id }), localFileInfo, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getUsersByOrganization(orgId, nameOrEmailPrefix, start, end) {
        const urlParams = { orgId };
        const url = generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/users`, urlParams);
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider, { nameOrEmailPrefix, start, end }));
        return response.data;
    }
    async getUserByOrganizationAndId(orgId, userId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId", { orgId, userId }), authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async inviteUser(orgId, user) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users", { orgId }), user, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateUser(orgId, userId, user) {
        var _a, _b, _c;
        try {
            await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId", { orgId, userId }), user, authenticatedApiConfig(this.authenticationProvider));
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async getUserAvatarUploadUrl(orgId, userId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId/avatarUploadUrl", {
            orgId,
            userId,
        }), localFileInfo, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async forcePasswordUpdateForUser(orgId, userId) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId/$force_password_change", {
            orgId,
            userId,
        }), {}, authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async deleteUser(orgId, userId) {
        const response = await axios.delete(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId", { orgId, userId }), authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async getEpicClientIds(orgId, overrideAgentBaseUrl) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/epic-client-ids", { orgId });
        if (overrideAgentBaseUrl) {
            url += `?overrideAgentBaseUrl=${overrideAgentBaseUrl}`;
        }
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(EpicClientIds, response.data);
    }
    async getAgentStatus(orgId, overrideAgentBaseUrl) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/agent-status", { orgId });
        if (overrideAgentBaseUrl) {
            url += `?overrideAgentBaseUrl=${overrideAgentBaseUrl}`;
        }
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(AgentStatus, response.data);
    }
    async getAgentConfig(orgId, overrideAgentBaseUrl) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/agent-config", { orgId });
        if (overrideAgentBaseUrl) {
            url += `?overrideAgentBaseUrl=${overrideAgentBaseUrl}`;
        }
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(AgentConfigurationDto, response.data);
    }
    async updateAgentConfig(orgId, input, overrideAgentBaseUrl) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/agent-config", { orgId });
        if (overrideAgentBaseUrl) {
            url += `?overrideAgentBaseUrl=${overrideAgentBaseUrl}`;
        }
        const response = await axios.put(url, input, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(AgentConfigurationDto, response.data);
    }
    async getAgentDeploymentConfig(orgId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/agent/deployconfig", { orgId });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(AgentDeploymentConfigDto, response.data);
    }
    async updateAgentDeploymentConfig(orgId, input) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/agent/deployconfig", { orgId });
        const response = await axios.put(url, input, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(AgentDeploymentConfigDto, response.data);
    }
    async sendInviteToWalletEmail(orgId, userId, invitePath) {
        await axios.post(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId/send", { orgId, userId }), { invitePath }, authenticatedApiConfig(this.authenticationProvider));
        console.log("Invite: ", invitePath);
    }
    async uploadFileToUrl(url, file, onUploadProgress) {
        let config;
        if (onUploadProgress) {
            config = {
                onUploadProgress: (progressEvent) => onUploadProgress(progressEvent.total ? (progressEvent.loaded / progressEvent.total) * 100 : 0),
            };
        }
        return await axios.put(url, file, config);
    }
    async getAllAPIKeys(orgId, start, end) {
        const urlParams = {
            orgId,
        };
        if (start !== undefined)
            urlParams.start = String(start);
        if (end !== undefined)
            urlParams.end = String(end);
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys?start=:start&end=:end", urlParams), authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(APIKeysResult, response.data);
    }
    async createAPIKey(orgId, keyName, permissions) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys", { orgId }), {
            keyName,
            permissions,
        }, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateAPIKey(orgId, keyId, keyName, status, permissions) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys/:keyId", { orgId, keyId }), {
            keyName,
            status,
            permissions,
        }, authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async deleteAPIKey(orgId, keyId) {
        const response = await axios.delete(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys/:keyId", { orgId, keyId }), authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async getDataSources(orgId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources", { orgId });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DataSourceDtoWithInProgressClassification, response.data);
    }
    async getDatasetsForDataSource(orgId, dataSourceId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/datasets", {
            orgId,
            dataSourceId,
        });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DatasetDto, response.data);
    }
    async registerDatasetForBigQueryDataSource(orgId, dataSourceId, datasetId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/datasets/:datasetId/register", { orgId, dataSourceId, datasetId });
        return await axios.put(url, {}, authenticatedApiConfig(this.authenticationProvider));
    }
    async getTablesOfDataSource(orgId, dataSourceId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/tables", {
            orgId,
            dataSourceId,
        });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(TableDto, response.data);
    }
    async registerTablesForDataSource(orgId, dataSourceId, input) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/register", {
            orgId,
            dataSourceId,
        });
        return await axios.post(url, input, authenticatedApiConfig(this.authenticationProvider));
    }
    async deleteDataSource(orgId, dataSourceId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId", { orgId, dataSourceId });
        return await axios.delete(url, authenticatedApiConfig(this.authenticationProvider));
    }
    async createDataSource(orgId, dataSource) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources", { orgId });
        const response = await axios.post(url, dataSource, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateDataSource(orgId, dataSourceId, dataSource) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId", { orgId, dataSourceId });
        const response = await axios.put(url, dataSource, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getRecentlyViewedDataSources(orgId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/recently-viewed", { orgId });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async searchDataSource(orgId, input) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/search", { orgId });
        const response = await axios.post(url, input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateDataSourceAsViewed(orgId, dataSourceId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/mark-viewed", {
            orgId,
            dataSourceId,
        });
        return await axios.put(url, {}, authenticatedApiConfig(this.authenticationProvider));
    }
    async getClassifications(orgId, dataSourceId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/classifications", {
            orgId,
            dataSourceId,
        });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async runClassification(orgId, dataSourceId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/classify-tables", {
            orgId,
            dataSourceId,
        });
        return await axios.post(url, {}, authenticatedApiConfig(this.authenticationProvider));
    }
    async getClassificationRun(orgId, runId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/classification/:runId", {
            orgId,
            runId,
        });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateClassificationRun(orgId, runId, input) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/classification/:runId", {
            orgId,
            runId,
        });
        return await axios.put(url, input, authenticatedApiConfig(this.authenticationProvider));
    }
    async updateColumnClassification(orgId, runId, entityId, columnId, input) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/classification/:runId/entity/:entityId/columns/:columnId", {
            orgId,
            runId,
            entityId,
            columnId,
        });
        return await axios.put(url, input, authenticatedApiConfig(this.authenticationProvider));
    }
    async updateDataWarehouseClassificationEntity(orgId, runId, entityId, input) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/classification/:runId/entity/:entityId", {
            orgId,
            runId,
            entityId,
        });
        return await axios.put(url, input, authenticatedApiConfig(this.authenticationProvider));
    }
    async getTagMetadata(orgId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/tag-metadata", { orgId });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async deleteClassificationRun(orgId, runId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/classification/:runId", {
            orgId,
            runId,
        });
        return await axios.delete(url, authenticatedApiConfig(this.authenticationProvider));
    }
    async getEnforcementViews(orgId, dataSourceId) {
        const url = generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/datasources/:dataSourceId/views", {
            orgId,
            dataSourceId,
        });
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getAWSDataDeliveryInfo() {
        const url = generateURL(this.environment.API_BASE_URL, "/aws-data-delivery-info");
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(AWSDataDeliveryInfoDto, response.data);
    }
}
